import { useState, useEffect, useCallback, useMemo } from 'react'

import { SchemaOf } from 'yup'

type Values = {
  [field: string]: any
}

type ValidationErrors<T> = {
  [K in keyof T]?: T[K] extends object ? ValidationErrors<T[K]> : string
}

type ValidationResult<T> = {
  errors: ValidationErrors<T>,
  isValid: boolean,
}

function useYup<T extends Values>(
  values: T,
  validationSchema: SchemaOf<any>
) {
  const [errors, setErrors] = useState<ValidationErrors<T> | null>(null)
  const isValid = errors && Object.keys(errors).length === 0;

  const validate = useCallback(() => {
    return validationSchema
      .validate(values, { abortEarly: false })
      .then(() => {
        return {} as ValidationErrors<T>
      })
      .catch((error: any) => {
        return yupToValidationErrors<T>(error)
      })
      .then(newErrors => {
        setErrors(newErrors)
        return {
          errors: newErrors,
          isValid: Object.keys(newErrors).length === 0
        } as ValidationResult<T>
      })
  }, [values])

  useEffect(
    () => { validate() },
    [values]
  )

  return {
    validate,
    errors: isEmpty(errors) ? null : errors,
    isValid,
  }
}

/**
 * Transform Yup errors to a ValidationErrors object
 */
function yupToValidationErrors<T extends Values>(
  yupError: any
): ValidationErrors<T> {
  let errors: any = {} as ValidationErrors<Values>
  if (yupError.inner.length === 0) {
    updateIn(errors, yupError.path, yupError.message)
    return errors
  }
  for (let err of yupError.inner) {
    updateIn(errors, err.path, err.message)
  }
  return errors
}

function updateIn(obj: any, path: string, value: any): any {
  const pathArray = path.split('.')
  let destinationObject = obj
  for (let i = 0; i < pathArray.length - 1; i++) {
    if (pathArray[i] in destinationObject === false) {
      destinationObject[pathArray[i]] = {}
    }
    destinationObject = destinationObject[pathArray[i]]
  }
  destinationObject[pathArray[pathArray.length - 1]] = value
}

const isEmpty = (value: any) => {
  return (
    value == null || // From standard.js: Always use === - but obj == null is allowed to check null || undefined
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  )
}

export default useYup
